import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { 
  creditActions, 
  simulateCreditScoreImprovement, 
  getCreditScoreTier 
} from '@/utils/supreme-intelligence/creditScoreProjections';
import { EnhancedUnifiedFormData } from '@/validation/enhancedUnifiedSchemas';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { TrendingUp, Calendar, Info } from 'lucide-react';

interface CreditScoreSimulatorProps {
  formData: EnhancedUnifiedFormData;
}

export const CreditScoreSimulator: React.FC<CreditScoreSimulatorProps> = ({ formData }) => {
  const currentScore = formData.personalDetails.creditScore;
  const [selectedActionIds, setSelectedActionIds] = useState<string[]>(['pay-down-credit-cards']);
  const [targetMonths, setTargetMonths] = useState(12);
  const [confidenceLevel, setConfidenceLevel] = useState<'conservative' | 'realistic' | 'optimistic'>('realistic');

  // Check if we have debt data
  const hasDebtData = formData.debts.autoLoanPayment > 0 || 
                      formData.debts.creditCardPayment > 0 ||
                      formData.debts.studentLoanBalance > 0;

  const selectedActions = creditActions.filter(a => selectedActionIds.includes(a.id));
  const projection = simulateCreditScoreImprovement(currentScore, selectedActions, targetMonths, confidenceLevel);

  const currentTier = getCreditScoreTier(currentScore);
  const targetTier = getCreditScoreTier(projection.targetScore);

  const chartData = projection.monthlyProjection.map(m => ({
    month: `Month ${m.month}`,
    score: m.score,
    low: m.confidenceRange.low,
    high: m.confidenceRange.high
  }));

  const toggleAction = (actionId: string) => {
    setSelectedActionIds(prev => 
      prev.includes(actionId) 
        ? prev.filter(id => id !== actionId)
        : [...prev, actionId]
    );
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-xl font-semibold mb-4 text-foreground">Credit Score Simulator</h3>

        {!hasDebtData && (
          <Alert className="mb-4">
            <Info className="h-4 w-4" />
            <AlertTitle>Limited Data Available</AlertTitle>
            <AlertDescription>
              For more accurate debt paydown recommendations, use the Unified Calculator which captures your complete debt profile.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <div className="mb-4">
              <Label>Timeline: {targetMonths} months</Label>
              <Slider
                value={[targetMonths]}
                onValueChange={([value]) => setTargetMonths(value)}
                min={3}
                max={24}
                step={3}
                className="mt-2"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-base">Select Actions to Take</Label>
              {creditActions.map((action) => {
                const Icon = action.icon;
                return (
                  <div key={action.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/50">
                    <Checkbox
                      id={action.id}
                      checked={selectedActionIds.includes(action.id)}
                      onCheckedChange={() => toggleAction(action.id)}
                    />
                    <div className="flex-1">
                      <label htmlFor={action.id} className="text-sm font-medium cursor-pointer flex items-center gap-2">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        {action.name}
                      </label>
                      <p className="text-xs text-muted-foreground mt-1">{action.description}</p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="secondary" className="text-xs">
                          +{action.estimatedImpact} pts
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          {action.timeToImpact === 0 ? 'Immediate' : `${action.timeToImpact} months`}
                        </Badge>
                        <Badge variant="secondary" className="text-xs capitalize">
                          {action.difficulty}
                        </Badge>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 rounded-lg bg-muted/50">
                <div className="text-sm text-muted-foreground mb-1">Current Score</div>
                <div className="text-3xl font-bold text-foreground">{currentScore}</div>
                <div className="text-xs text-muted-foreground">{currentTier.tier}</div>
              </div>

              <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                <div className="text-sm text-muted-foreground mb-1">Projected Score</div>
                <div className="text-3xl font-bold text-primary">{projection.targetScore}</div>
                <div className="text-xs text-primary">
                  {targetTier.tier} (+{projection.totalPointsGained} pts)
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="font-medium mb-3 text-foreground">Credit Score Projection</h4>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    interval={Math.floor(targetMonths / 6)}
                  />
                  <YAxis 
                    domain={[Math.max(300, currentScore - 50), Math.min(850, projection.targetScore + 50)]}
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="high" 
                    stroke="hsl(var(--primary))" 
                    fill="hsl(var(--primary) / 0.1)" 
                  />
                  <Area 
                    type="monotone" 
                    dataKey="low" 
                    stroke="hsl(var(--primary))" 
                    fill="hsl(var(--primary) / 0.1)" 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="score" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={3}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {projection.interestRateSavings > 0 && (
              <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <h4 className="font-semibold text-green-900 dark:text-green-100">Financial Impact</h4>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-green-800 dark:text-green-200">Interest Rate Savings</div>
                    <div className="text-2xl font-bold text-green-900 dark:text-green-100">
                      {projection.interestRateSavings.toFixed(2)}%
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-green-800 dark:text-green-200">Lifetime Savings</div>
                    <div className="text-2xl font-bold text-green-900 dark:text-green-100">
                      ${projection.lifetimeSavings.toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-green-700 dark:text-green-300 mt-2">
                  Based on a $320,000 loan over 30 years
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
